import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, type HelpdeskTicket, type ID } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { useMut } from '../../../lib/query';
import { Avatar, Badge, Button, DataTable, DescList, Loading, Modal, Row, Screen, PageHeader, Select, Stack, StatusBadge, Text, TextField, radius, useTheme } from '../../../ui';

/** Buttons default to alignSelf flex-start; bottom-align them with labelled fields. */
const END = { alignSelf: 'flex-end' as const };

const STATUSES: HelpdeskTicket['status'][] = ['open', 'in_progress', 'resolved', 'closed'];
const CATS: HelpdeskTicket['category'][] = ['payroll', 'leave', 'attendance', 'it', 'policy', 'other'];
const catLabel = (c: string) => (c === 'it' ? 'IT' : humanize(c));
const prioTone = (p: HelpdeskTicket['priority']) => (p === 'high' ? 'danger' : p === 'medium' ? 'warning' : 'neutral');

function TicketSheet({ id, onClose }: { id: ID | null; onClose: () => void }) {
  const { colors } = useTheme();
  const { me } = useAuth();
  const q = useQuery({ queryKey: ['tickets', 'one', id], queryFn: () => api.ess.tickets.get(id!), enabled: !!id });
  const [body, setBody] = useState('');
  const comment = useMut((b: string) => api.ess.tickets.comment(id!, b), { invalidate: [['tickets']], success: 'Reply sent', onSuccess: () => setBody('') });
  const update = useMut((b: Partial<HelpdeskTicket>) => api.ess.tickets.update(id!, b), { invalidate: [['tickets']], success: 'Ticket updated' });
  const t = q.data;
  return (
    <Modal open={!!id} onClose={onClose} title={t ? `${t.number} · ${t.subject}` : 'Ticket'} width={600} testID="ticket-sheet">
      {!t ? (
        <Loading />
      ) : (
        <>
          <Row>
            <StatusBadge status={t.status} />
            <Badge tone={prioTone(t.priority)}>{`${humanize(t.priority)} priority`}</Badge>
            <Badge tone="info">{catLabel(t.category)}</Badge>
          </Row>
          <DescList items={[['Raised by', t.employee ? `${t.employee.full_name} (${t.employee.emp_code})` : '—'], ['Created', `${fmtDate(t.created_at)} ${fmtTime(t.created_at)}`], ['Assignee', t.assignee?.name ?? 'Unassigned']]} />
          <Row align="flex-end">
            <Select containerStyle={{ minWidth: 170 }} label="Status" value={t.status} options={STATUSES.map((s) => ({ value: s, label: humanize(s) }))} onChange={(status) => update.mutate({ status })} testID="ticket-status" />
            <Select containerStyle={{ minWidth: 150 }} label="Priority" value={t.priority} options={(['low', 'medium', 'high'] as const).map((s) => ({ value: s, label: humanize(s) }))} onChange={(priority) => update.mutate({ priority })} />
            {me && t.assignee_id !== me.id && <Button icon="user" title="Assign to me" style={END} loading={update.isPending} onPress={() => update.mutate({ assignee_id: me.id, status: t.status === 'open' ? 'in_progress' : t.status })} />}
          </Row>
          <View style={{ padding: 12, borderRadius: radius.md, backgroundColor: colors.surface2 }}>
            <Text>{t.description}</Text>
          </View>
          <Text variant="title">Conversation</Text>
          {(t.comments ?? []).length === 0 && <Text variant="muted">No replies yet.</Text>}
          {(t.comments ?? []).map((c) => (
            <Row key={c.id} align="flex-start" wrap={false}>
              <Avatar name={c.user.name} size={28} />
              <View style={{ flex: 1 }}>
                <Text weight="semibold">
                  {c.user.name} <Text variant="small">{fmtDate(c.created_at)} {fmtTime(c.created_at)}</Text>
                </Text>
                <Text>{c.body}</Text>
              </View>
            </Row>
          ))}
          {t.status !== 'closed' && (
            <Stack gap={8}>
              <TextField multiline placeholder="Write a reply…" accessibilityLabel="Reply" value={body} onChangeText={setBody} testID="ticket-reply" />
              <Row justify="flex-end">
                <Button variant="primary" icon="send" title="Reply" disabled={!body.trim()} loading={comment.isPending} onPress={() => comment.mutate(body.trim())} />
              </Row>
            </Stack>
          )}
        </>
      )}
    </Modal>
  );
}

type F = { status: string; category: string; priority: string; assigned: '' | 'me' };

/** HR helpdesk queue: every ticket, filters, assign to me, status change, replies. */
export function HelpdeskQueueScreen() {
  const { me } = useAuth();
  const [f, setF] = useState<F>({ status: 'open', category: '', priority: '', assigned: '' });
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<ID | null>(null);
  const params = { scope: 'all', status: f.status || undefined, category: f.category || undefined, priority: f.priority || undefined, assignee_id: f.assigned === 'me' ? me?.id : undefined, page };
  const q = useQuery({ queryKey: ['tickets', 'queue', params], queryFn: () => api.ess.tickets.list(params) });
  const assign = useMut((id: ID) => api.ess.tickets.update(id, { assignee_id: me!.id, status: 'in_progress' }), { invalidate: [['tickets']], success: 'Assigned to you' });
  const set = <K extends keyof F>(k: K, v: F[K]) => {
    setF({ ...f, [k]: v });
    setPage(1);
  };
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="helpdesk-queue">
      <PageHeader title="Helpdesk queue" subtitle={q.data && !Array.isArray(q.data) ? `${q.data.total} tickets` : undefined} />
      <Row gap={8}>
        <Select containerStyle={{ flexGrow: 1, flexBasis: 150 }} value={f.status} onChange={(v) => set('status', v)} options={[{ value: '', label: 'All statuses' }, ...STATUSES.map((s) => ({ value: s, label: humanize(s) }))]} testID="queue-status" />
        <Select containerStyle={{ flexGrow: 1, flexBasis: 150 }} value={f.category} onChange={(v) => set('category', v)} options={[{ value: '', label: 'All categories' }, ...CATS.map((s) => ({ value: s, label: catLabel(s) }))]} />
        <Select containerStyle={{ flexGrow: 1, flexBasis: 150 }} value={f.priority} onChange={(v) => set('priority', v)} options={[{ value: '', label: 'All priorities' }, ...['high', 'medium', 'low'].map((s) => ({ value: s, label: humanize(s) }))]} />
        <Select<'' | 'me'> containerStyle={{ flexGrow: 1, flexBasis: 150 }} value={f.assigned} onChange={(v) => set('assigned', v)} options={[{ value: '', label: 'Anyone' }, { value: 'me', label: 'Assigned to me' }]} />
      </Row>
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={(t) => setSel(t.id)}
        emptyTitle="Queue is empty"
        testID="queue-table"
        columns={[
          { key: 'number', title: '#', width: 100 },
          { key: 'subject', title: 'Subject', width: 200, flex: 2 },
          { key: 'employee', title: 'Raised by', width: 150, sortValue: (t) => t.employee?.full_name, render: (t) => t.employee?.full_name ?? '—' },
          { key: 'category', title: 'Category', width: 100, render: (t) => catLabel(t.category), sortValue: (t) => t.category },
          { key: 'priority', title: 'Priority', width: 90, sortValue: (t) => ({ high: 0, medium: 1, low: 2 })[t.priority], render: (t) => <Badge tone={prioTone(t.priority)}>{humanize(t.priority)}</Badge> },
          { key: 'status', title: 'Status', width: 110, render: (t) => <StatusBadge status={t.status} />, sortValue: (t) => t.status },
          { key: 'assignee', title: 'Assignee', width: 130, sortValue: (t) => t.assignee?.name, render: (t) => t.assignee?.name ?? <Button size="sm" title="Take" loading={assign.isPending && assign.variables === t.id} onPress={() => assign.mutate(t.id)} /> },
          { key: 'created_at', title: 'Created', width: 110, render: (t) => fmtDate(t.created_at), sortValue: (t) => t.created_at },
        ]}
      />
      <TicketSheet id={sel} onClose={() => setSel(null)} />
    </Screen>
  );
}
